import classnames from 'classnames';
import React, {useEffect, useState} from 'react';

import {Anchor, Button, Heading, Icon, TabNavigation, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from '@albert-io/models';
import {getClassroomDomainBackgroundClass} from 'client/Classrooms/classroomDomainColor';
import {history} from 'client/history';

import {AutoArchiveChip, isAutoArchivingSoon} from './AutoArchiveChip';

export const getClassDetailHeaderQuery = (classroomId: string) =>
  resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classroomId])
    .include('subjects_v2.curriculum_area_v1');

// Dark translucent pills per the design; no on-color button variant exists in atomic yet.
const ON_COLOR_BUTTON_CLASSES =
  'border-none! bg-[rgb(0_0_0/0.25)]! content-primary-inverse-static! hover:bg-[rgb(0_0_0/0.35)]!';

interface Props {
  classroomId: string;
}

/**
 * Class banner with archive state, Invite/Settings entry points, and the
 * class tab bar. Rendered in TeacherDashboardLayout's header slot.
 */
export const ClassDetailHeader = ({classroomId}: Props) => {
  const [classroom, setClassroom] = useState<ClassroomModelV1 | null>(null);

  useEffect(() => {
    let isStale = false;
    getClassDetailHeaderQuery(classroomId)
      .getResourcePromise()
      .then((fetchedClassroom) => {
        if (!isStale) {
          setClassroom(fetchedClassroom as ClassroomModelV1);
        }
      })
      // Banner is decorative (name + color); an invalid class already redirects to /classes.
      .catch(() => {});
    return () => {
      isStale = true;
    };
  }, [classroomId]);

  const isArchived = Boolean(classroom?.isArchived?.());
  const isOnColor = Boolean(classroom);
  const settingsPath = `/classes/${classroomId}/settings/details`;

  return (
    <div className='shrink-0'>
      <header
        className={classnames(
          'flex min-h-[92px] items-center gap-x2 px-x4 py-x4',
          isOnColor ? getClassroomDomainBackgroundClass(classroom) : 'background-bold'
        )}
      >
        <div className='min-w-0 flex grow flex-col items-start gap-x1'>
          {!isArchived && classroom && isAutoArchivingSoon(classroom) && (
            <AutoArchiveChip classroom={classroom} />
          )}
          <Heading
            as='h1'
            className={classnames('m-x0 w-full truncate', {
              'content-primary-inverse-static': isOnColor
            })}
            color='inherit'
            size='m'
          >
            {classroom?.getName() ?? ''}
          </Heading>
        </div>
        <div className='flex shrink-0 items-center gap-x1'>
          {!isArchived && (
            <Button
              className={ON_COLOR_BUTTON_CLASSES}
              roundness='pill'
              onClick={() => history.pushState(null, `/classes/${classroomId}/people`)}
            >
              <Icon className='mr-x1' icon='user-plus' />
              Invite
            </Button>
          )}
          <Button
            className={ON_COLOR_BUTTON_CLASSES}
            roundness='pill'
            onClick={() => history.pushState(null, settingsPath)}
          >
            <Icon className='mr-x1' icon='cog' />
            Settings
          </Button>
        </div>
      </header>
      {isArchived && (
        <div className='flex items-center gap-x2 background-warning-subtle px-x4 py-x2'>
          <Icon className='content-warning' icon='archive' iconStyle='regular' />
          <Text className='content-warning' size='s'>
            This class is archived. You can unarchive in the{' '}
            <Anchor
              as='button'
              inheritColor
              underlined
              onClick={() => history.pushState(null, settingsPath)}
            >
              class settings
            </Anchor>
            .
          </Text>
        </div>
      )}
      <div className='border-b border-primary px-x2'>
        <TabNavigation align='left' paddingLevel={0}>
          <TabNavigation.Tab isActive>
            <Icon className='mr-x1' icon='file-lines' iconStyle='regular' />
            Assignments
          </TabNavigation.Tab>
          <TabNavigation.Tab
            onClick={() => history.pushState(null, `/classes/${classroomId}/subjects`)}
          >
            <Icon className='mr-x1' icon='books' iconStyle='regular' />
            Subjects
          </TabNavigation.Tab>
          <TabNavigation.Tab
            onClick={() => history.pushState(null, `/classes/${classroomId}/students/overview`)}
          >
            <Icon className='mr-x1' icon='chart-pie' iconStyle='regular' />
            Overview
          </TabNavigation.Tab>
        </TabNavigation>
      </div>
    </div>
  );
};
